import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { SebBuild } from '../../../@resources/anim/SebBuild';
import { SebComponent } from '../../../@resources/anim/SebCommon';
import { SebMeeting } from '../../../@resources/anim/SebMeeting';
import { Colors, Grid, usePrompt } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { TextButton } from '../../../ui/TextButton';
import { useResolve } from '../../Container';
import { Copy, Flexer, Spacer, Title } from '../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../popups/Popup';
import { PreferencesManager } from '../../profile/PreferencesManager';
import { DesignMode } from './DesignMode';
import { MeetingMode } from './MeetingMode';


const Divide = styled.div`
    position: relative;
    margin: 1.5rem 1rem;
    height: .0625rem;
    background: ${Colors.border};

    &:before {
        content: 'or';
        padding: 0 .5rem;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        font-size: 1rem;
        font-weight: 400;
        color: ${Colors.textLighter};
        background: white;
    }
`;

const OptionDropButton = styled(DropButton)`
    white-space: initial;
    margin: 0 1rem;
`;

const DontShowButton = styled(TextButton)`
    font-size: .75rem;
    color: ${Colors.text};
    text-decoration: underline;

    &:hover, &:active {
        color: ${Colors.textDark};
        text-decoration: underline;
    }
`;

type OptionProps = { seb: SebComponent, label: string, desc: string, onSelect: () => void };
const Option = ({ seb: Seb, label, desc, onSelect }: OptionProps) => {
    const [active, setActive] = useState(false);
    const onEnter = () => setActive(true);
    const onLeave = () => setActive(false);
    return <Flexer onMouseEnter={onEnter} onMouseLeave={onLeave} onClick={onSelect}>
        <OptionDropButton overflow=".5" padding=".5 1.5">
            <Flexer flex="row" gap="1" padding=".5">
                <Flexer shrink width="4">
                    <Seb active={active} />
                </Flexer>
                <Flexer flex="column" gap=".5">
                    <Copy bold flush size="1.25">{label}</Copy>
                    <Copy flush size=".875">{desc}</Copy>
                </Flexer>
            </Flexer>
        </OptionDropButton>
    </Flexer>;
};

export const editorModePopup = () => ({ ui: EditorModePopup });

export const EditorModePopup = React.memo<PopupProps>(({ action, dismiss }) => {

    const preferences = useResolve(PreferencesManager);

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: dismiss,
    });

    const onIgnore = useCallback(() => {
        preferences.patch({ editorModePopup: false });
        dismiss();
    }, [dismiss]);

    return <Popup ref={promptRef} w={32}>
        <Flexer padding="1">
            <Flexer flex="column" center>
                <Title color={Colors.textLight} flush>Welcome to your new board!</Title>
                <Copy color={Colors.textLight} size=".8125" flush>Let&apos;s get started!  Do you want to...</Copy>
            </Flexer>
            <Spacer f={3} />
            <Grid columns="1" rows="1fr auto 1fr">
                <Option
                    seb={SebBuild}
                    label="Customize the board"
                    desc="Use Design Mode to customize the board design before you get going."
                    onSelect={() => action(DesignMode.id)}
                />
                <Divide />
                <Option
                    seb={SebMeeting}
                    label="Start a new meeting"
                    desc="Switch to Meeting Mode and dive straight into the board with your team."
                    onSelect={() => action(MeetingMode.id)}
                />
            </Grid>
        </Flexer>
        <Flexer flex center margin="1 0 0">
            <DontShowButton onClick={onIgnore}>{'Don\'t show this anymore'}</DontShowButton>
        </Flexer>
    </Popup>;
});
